import { ROUTES } from 'constants/routePaths';
import { visibleNav } from 'util/visibleNav';

/**
 * Where to send someone who has no Dashboard.
 *
 * The dashboard is an ordinary module now, so `dashboard:read` can be withheld
 * — and every post-login redirect used to point at `/dashboard` unconditionally,
 * which would strand those users on a page they may not open. This resolves the
 * FIRST module they actually hold, in the order their own panel lists it, so the
 * landing page is somewhere they can work rather than an error.
 *
 * Returns `null` when they hold nothing at all; callers show the "no modules"
 * page in that case (see views/common/NoModules).
 *
 * Never returns the dashboard unless the caller genuinely holds `dashboard:read`
 * — otherwise a guard redirecting here would bounce straight back and loop.
 */
export function resolveLandingPath({
  can,
  role,
  principal,
  hasCenter = false,
  assignments = [],
  hasResourceGrant,
  permsLoaded = true,
  roleNames = [],
}) {
  if (typeof can !== 'function') return null;
  // Until the permission payload lands, assume the dashboard: it is the common
  // case and avoids a redirect flash on every page load.
  if (!permsLoaded) return ROUTES.DASHBOARD;
  if (can('dashboard', 'read')) return ROUTES.DASHBOARD;

  // The SAME list the sidebar renders (util/visibleNav), not a second walk of
  // the role's static layout. The old walk missed modules a role was GRANTED
  // but whose own panel never declared them, so a mentor holding only
  // `mentors:read` — who sees Stakeholders in their sidebar — was sent here and
  // got `null`, i.e. the "no modules" page while holding a module.
  //
  // It also missed the sidebar's own rules (My Reviews is hidden from admins,
  // Workspace from collaborators, Programs opens on a collaboration grant), so
  // the landing page could be somewhere the menu does not even list.
  const { items } = visibleNav({
    role,
    principal,
    hasCenter,
    assignments,
    roleNames,
    can,
    hasResourceGrant,
    permsLoaded,
  });
  const first = items.find((item) => item.key !== 'overview');
  return first ? first.path : null;
}

export default resolveLandingPath;
